import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { InfoText, InlineNotificationType, TextNodeType } from '@mwe/models';
import { NgClass } from '@angular/common';
import { HeadlineComponent } from '../headline/headline.component';
import { InfoTextIconComponent } from '../info-text/info-text-icon.component';
import { ClickPreventDefaultDirective } from '../directives/click/click-prevent-default.directive';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '../buttons/button/button.component';
import { SafeHtmlPipe } from '@mwe/ui/pipes';
import ShortUniqueId from 'short-unique-id';

const ColorSchemes = {
  success: {
    borderClass: 'border-success inline-notification-box-shadow-success',
    backgroundClass: 'bg-green-010',
    iconColorClass: 'text-success',
  },
  danger: {
    borderClass: 'border-danger inline-notification-box-shadow-danger',
    backgroundClass: 'bg-red-050',
    iconColorClass: 'text-danger',
  },
  info: {
    borderClass: 'border-secondary-light inline-notification-box-shadow-secondary-light',
    backgroundClass: 'bg-blue-050',
    iconColorClass: 'text-secondary-light',
  },
  warning: {
    borderClass: 'border-warning inline-notification-box-shadow-warning',
    backgroundClass: 'bg-yellow-050',
    iconColorClass: 'text-warning',
  },
};

@Component({
  selector: 'mwe-inline-notification',
  templateUrl: './inline-notification.component.html',
  imports: [
    NgClass,
    HeadlineComponent,
    InfoTextIconComponent,
    ClickPreventDefaultDirective,
    TranslateDirective,
    ButtonComponent,
    TranslatePipe,
    SafeHtmlPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineNotificationComponent {
  private translateService = inject(TranslateService);

  readonly type = input<InlineNotificationType>('info');
  readonly titleKey = input<string>();
  readonly messageKey = input<string>();
  readonly messageKeys = input<string[]>();
  readonly showCloseButton = input<boolean>(false);
  readonly placeholderMap = input<Map<string, string>>();
  readonly link = input<string>();
  readonly iconClassOverwrite = input<string>('');
  readonly infoText = input<InfoText>();
  readonly isMinimalLayout = input<boolean>(false);
  readonly headingNodeType = input<TextNodeType>('h5');
  readonly role = input<string | null>('alert');
  readonly ctaLabelKey = input<string>();
  readonly ctaPrependIconClass = input<string>();
  readonly isCtaLoading = input<boolean>(false);

  readonly onLinkClick = output();
  readonly onCTAClick = output();
  readonly onCloseClick = output();

  readonly isVisible = signal(true);
  readonly uuid = signal<string>(new ShortUniqueId({ dictionary: 'alpha' }).rnd(8));

  readonly prependIconClass = computed<string>(() => (this.isCtaLoading() ? '' : this.ctaPrependIconClass()));
  readonly isOnlyHeadline = computed<boolean>(() => !this.messageKey() && !this.messageKeys() && !this.infoText());
  readonly colorScheme = computed(() => ColorSchemes[this.type()]);
  readonly showTitle = computed<boolean>(() => {
    try {
      return this.titleKey()?.length > 0 && this.translateService.instant(this.titleKey())?.length > 0;
    } catch (_) {
      return false;
    }
  });
  readonly iconClasses = computed<string>(() => {
    if (this.iconClassOverwrite()) {
      return this.iconClassOverwrite();
    }

    switch (this.type()) {
      case 'info':
        return 'fas fa-circle-info';
      case 'danger':
        return 'fas fa-circle-xmark';
      case 'warning':
        return 'fas fa-circle-exclamation';
      case 'success':
        return 'fas fa-circle-check';
      default:
        return '';
    }
  });
  readonly cssClasses = computed<string[]>(() => {
    const classes = [];

    classes.push(this.colorScheme().borderClass);
    classes.push(this.colorScheme().backgroundClass);

    if (!this.isVisible()) classes.push('d-none');

    return classes;
  });
  readonly translateValues = computed<{ [key: string]: string | number }>(() => {
    if (!this.placeholderMap()) return {};
    return [...this.placeholderMap().entries()]?.reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value,
      }),
      {},
    );
  });

  fireLinkClickEvent(): void {
    this.onLinkClick.emit();
  }

  onClose(): void {
    this.isVisible.set(false);
    this.onCloseClick.emit();
  }
}
